import { overlaysApi } from '../api/overlays';
import { OVERLAY_SIZE_FACTORS, ExportTarget } from '../types';

export interface FilterValues {
  brightness: number;
  contrast: number;
  saturation: number;
  grayscale: boolean;
}

export interface ReframeOffset {
  focusX: number; // 0..1, default 0.5
  focusY: number; // 0..1, default 0.5
}

export interface CropLike {
  x: number;
  y: number;
  width: number;
  height: number;
  enabled: boolean;
}

export type OverlayDrawConfig = { overlayId: string; anchor: string; size: string; opacity: number };

export const CENTER_REFRAME: ReframeOffset = { focusX: 0.5, focusY: 0.5 };

export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

export function buildCSSFilter(filters: FilterValues): string {
  const parts: string[] = [];
  if (filters.brightness !== 0) parts.push(`brightness(${1 + filters.brightness})`);
  if (filters.contrast !== 0) parts.push(`contrast(${1 + filters.contrast / 100})`);
  if (filters.saturation !== 0) parts.push(`saturate(${1 + filters.saturation})`);
  if (filters.grayscale) parts.push('grayscale(1)');
  return parts.join(' ');
}

/** Draws the (optionally cropped) original at full resolution with filters baked in. */
export function buildSourceCanvasFromImage(
  originalImg: HTMLImageElement,
  filters: FilterValues,
  crop?: CropLike,
  canvasWidth?: number,
): HTMLCanvasElement {
  const origW = originalImg.width;
  const origH = originalImg.height;

  let srcX = 0, srcY = 0, srcW = origW, srcH = origH;
  if (crop?.enabled && crop.width > 0 && crop.height > 0 && canvasWidth) {
    const scaleFromCanvas = origW / canvasWidth;
    srcX = crop.x * scaleFromCanvas;
    srcY = crop.y * scaleFromCanvas;
    srcW = crop.width * scaleFromCanvas;
    srcH = crop.height * scaleFromCanvas;
  }

  const canvas = document.createElement('canvas');
  canvas.width = srcW;
  canvas.height = srcH;
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  const cssFilter = buildCSSFilter(filters);
  if (cssFilter) ctx.filter = cssFilter;

  ctx.drawImage(originalImg, srcX, srcY, srcW, srcH, 0, 0, srcW, srcH);
  ctx.filter = 'none';

  return canvas;
}

/** Loads `imageUrl` and builds the source canvas (see `buildSourceCanvasFromImage`). */
export async function buildSourceCanvas(
  imageUrl: string,
  filters: FilterValues,
  crop?: CropLike,
  canvasWidth?: number,
): Promise<HTMLCanvasElement> {
  const originalImg = await loadImage(imageUrl);
  return buildSourceCanvasFromImage(originalImg, filters, crop, canvasWidth);
}

export function drawCoverFit(
  ctx: CanvasRenderingContext2D,
  source: HTMLCanvasElement | HTMLImageElement,
  targetW: number,
  targetH: number,
  focusX: number,
  focusY: number,
) {
  const srcW = source.width;
  const srcH = source.height;

  const coverScale = Math.max(targetW / srcW, targetH / srcH);
  const drawW = srcW * coverScale;
  const drawH = srcH * coverScale;

  const slackX = drawW - targetW;
  const slackY = drawH - targetH;

  const drawX = -focusX * slackX;
  const drawY = -focusY * slackY;

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.fillStyle = '#000000';
  ctx.fillRect(0, 0, targetW, targetH);
  ctx.drawImage(source, drawX, drawY, drawW, drawH);
}

export function drawOverlay(
  ctx: CanvasRenderingContext2D,
  overlayImg: HTMLImageElement,
  targetW: number,
  targetH: number,
  anchor: string,
  sizeFactor: number,
  opacity: number,
) {
  const shortSide = Math.min(targetW, targetH);
  const oTargetW = shortSide * sizeFactor;
  const oScale = oTargetW / overlayImg.width;
  const oW = overlayImg.width * oScale;
  const oH = overlayImg.height * oScale;
  const pad = shortSide * 0.03;

  let ox = 0, oy = 0;
  if (anchor.includes('left')) ox = pad;
  else if (anchor.includes('right')) ox = targetW - oW - pad;
  else ox = (targetW - oW) / 2;

  if (anchor.includes('top')) oy = pad;
  else if (anchor.includes('bottom')) oy = targetH - oH - pad;
  else oy = (targetH - oH) / 2;

  ctx.globalAlpha = opacity;
  ctx.drawImage(overlayImg, ox, oy, oW, oH);
  ctx.globalAlpha = 1;
}

export async function drawOverlayConfig(
  ctx: CanvasRenderingContext2D,
  config: OverlayDrawConfig,
  overlays: { _id: string }[],
  canvasW: number,
  canvasH: number,
) {
  const overlayData = overlays.find((o) => o._id === config.overlayId);
  if (!overlayData) return;
  const img = await loadImage(overlaysApi.fileUrl(overlayData._id));
  const sizeFactor = OVERLAY_SIZE_FACTORS[config.size as keyof typeof OVERLAY_SIZE_FACTORS] || 0.18;
  // Presets saved before anchor/size were validated server-side may be missing
  // them — default to bottom-right rather than crashing the whole export.
  drawOverlay(ctx, img, canvasW, canvasH, config.anchor || 'bottom-right', sizeFactor, config.opacity);
}

/** Full-resolution render of `sourceCanvas` into one export target: cover-fit resize + overlay compositing. */
export async function renderToTarget(
  target: ExportTarget,
  sourceCanvas: HTMLCanvasElement,
  overlayConfig: OverlayDrawConfig | null,
  overlays: { _id: string }[],
  reframe: ReframeOffset,
  brandingOverlayConfig: OverlayDrawConfig | null,
): Promise<Blob> {
  const outCanvas = document.createElement('canvas');
  outCanvas.width = target.width;
  outCanvas.height = target.height;
  const outCtx = outCanvas.getContext('2d')!;

  drawCoverFit(outCtx, sourceCanvas, target.width, target.height, reframe.focusX, reframe.focusY);

  // User's optional overlay drawn first (underneath branding)
  if (overlayConfig) {
    await drawOverlayConfig(outCtx, overlayConfig, overlays, target.width, target.height);
  }

  // Branding overlay always drawn on top
  if (brandingOverlayConfig) {
    await drawOverlayConfig(outCtx, brandingOverlayConfig, overlays, target.width, target.height);
  }

  return new Promise<Blob>((resolve, reject) => {
    outCanvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('toBlob failed'))),
      'image/jpeg',
      0.95,
    );
  });
}
